import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api';

/**
 * Folders API using RTK Query
 *
 * Provides folder management endpoints for organizing files within Knowledge Bases.
 * Supports both general (account-level) folders and segmented knowledge base folders.
 *
 * Uses centralized baseQuery for consistent error handling.
 */
export const foldersApi = createApi({
  reducerPath: 'foldersApi',
  baseQuery,
  tagTypes: ['Folders'],
  keepUnusedDataFor: 300, // 5 minutes cache
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    /**
     * Get list of folders
     * @param {Object} params - Query parameters
     * @param {string} [params.segmentedKnowledgeId] - Filter by segmented knowledge ID ('general' for account-level)
     */
    getFolders: builder.query({
      query: ({ segmentedKnowledgeId } = {}) => {
        const params = new URLSearchParams();
        if (segmentedKnowledgeId) {
          params.append('segmented_knowledge_id', segmentedKnowledgeId);
        }
        const queryString = params.toString();
        return `/api/v1/folders/${queryString ? `?${queryString}` : ''}`;
      },
      // LIST/ITEM pattern: provide both individual item tags and list tag
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Folders', id })),
              { type: 'Folders', id: 'LIST' },
            ]
          : [{ type: 'Folders', id: 'LIST' }],
    }),

    /**
     * Get single folder by ID
     */
    getFolderById: builder.query({
      query: (id) => `/api/v1/folders/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Folders', id }],
    }),

    /**
     * Create new folder
     * @param {Object} folderData - Folder data
     * @param {string} folderData.name - Folder name
     * @param {string} [folderData.segmented_knowledge] - Segmented knowledge ID (optional, for segmented KB)
     */
    createFolder: builder.mutation({
      query: (folderData) => ({
        url: '/api/v1/folders/',
        method: 'POST',
        body: folderData,
      }),
      // Only invalidate the LIST tag
      invalidatesTags: [{ type: 'Folders', id: 'LIST' }],
    }),

    /**
     * Update existing folder (rename)
     * @param {Object} params - Update parameters
     * @param {string} params.id - Folder ID
     * @param {string} params.name - New folder name
     */
    updateFolder: builder.mutation({
      query: ({ id, ...folderData }) => ({
        url: `/api/v1/folders/${id}/`,
        method: 'PATCH',
        body: folderData,
      }),
      // Invalidate both the specific item and the list
      invalidatesTags: (result, error, { id }) => [
        { type: 'Folders', id },
        { type: 'Folders', id: 'LIST' },
      ],
    }),

    /**
     * Delete folder (must be empty)
     * @param {string} id - Folder ID
     */
    deleteFolder: builder.mutation({
      query: (id) => ({
        url: `/api/v1/folders/${id}/`,
        method: 'DELETE',
      }),
      // Invalidate both the specific item and the list
      invalidatesTags: (result, error, id) => [
        { type: 'Folders', id },
        { type: 'Folders', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetFoldersQuery,
  useGetFolderByIdQuery,
  useCreateFolderMutation,
  useUpdateFolderMutation,
  useDeleteFolderMutation,
} = foldersApi;
